<template>
  <Modal
    title="Schedule New Appointment"
    submit-btn-text="Schedule"
    :saving="saving"
    @hide-modal="emit('hide-modal')"
    @submit="handleSave"
  >
    <div class="flex flex-col gap-3">
      <CustomerSearchInput
        :validation="
          firstErrorMessage(v$.customer_id) ||
          firstErrorMessage(v$.address_id) ||
          ''
        "
        @update:modelValue="handleCustomerSelect"
      />
      <div v-if="selectedAddress && selectedCustomer">
        <div class="flex flex-col">
          <span class="font-medium">{{
            selectedCustomer.flattened_name ||
            `${selectedCustomer.first_name} ${selectedCustomer.last_name}`
          }}</span>
          <span class="text-xs text-gray-500 pointer-events-none">{{
            fullAddress(selectedAddress)
          }}</span>
        </div>
      </div>
      <div class="flex flex-col md:flex-row items-start gap-3 w-full">
        <SelectInput
          v-model="formData.service_category_id"
          label="Service Category"
          :options="serviceCategoryOptions"
          :validation="firstErrorMessage(v$.service_category_id) ? 'error' : ''"
          :helper-text="firstErrorMessage(v$.service_category_id)"
          :filter="true"
          filter-placeholder="Search categories..."
        />
        <div class="w-full relative">
          <Checkbox
            v-model="allTechs"
            label="Include all technicians"
            class="absolute right-0 top-0"
          ></Checkbox>
          <SelectInput
            :model-value="formData.technician_id"
            label="Technician"
            :options="technicianOptions"
            placeholder="Auto Assign"
            :filter="true"
            filter-placeholder="Search technicians..."
            :multiselect="isMultiTechEnabled"
            :max-selections="isMultiTechEnabled ? MAX_TECHS : undefined"
            :helper-text="limitHelperText"
            @update:modelValue="handleTechnicianSelect"
          />
        </div>
      </div>
      <div v-if="selectedAddress && selectedServiceCategory">
        <AppointmentAvailabilityInput
          :service-category-id="formData.service_category_id"
          :duration-minutes="selectedServiceCategory?.duration"
          :zipcode="selectedAddress?.zip_code"
          :user-id="selectedTech?.user_id"
          :user-ids="isMultiTechEnabled ? selectedTechIds : undefined"
          :validation="firstErrorMessage(v$.scheduled_time)"
          :scheduled_time="formData.scheduled_time"
          :arrival_window_start="formData.arrival_window_start"
          :arrival_window_end="formData.arrival_window_end"
          :unavailable_time="formData.unavailable_time"
          @update:modelValue="handleTimeSelected"
        />
      </div>
      <TextArea v-model="formData.notes" message="Notes" />
      <div class="flex flex-col md:flex-row gap-3">
        <ChoiceBoxV2
          v-model="formData.lead_type"
          name="Lead Type"
          label="Lead Type"
          type="lead_types"
          placeholder="None"
        />
        <ChoiceBoxV2
          v-model="formData.lead_source"
          name="Lead Source"
          label="Lead Source"
          type="lead_sources"
          placeholder="None"
        />
      </div>
    </div>
    <template #delete>
      <Toggle
        :checked="!formData.skip_notifications"
        label-after="Notify customer of this appointment"
        @update:checked="(e) => (formData.skip_notifications = !e)"
      />
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import ChoiceBoxV2 from "@/components/ChoiceBoxV2.vue";
import Modal from "@/components/Modal.vue";
import Checkbox from "@/components/Seranova/Checkbox/Checkbox.vue";
import SelectInput from "@/components/Seranova/SelectInput/SelectInput.vue";
import TextArea from "@/components/Seranova/TextArea/TextArea.vue";
import Toggle from "@/components/Seranova/Toggle/Toggle.vue";
import { useMultiTechSelect } from "@/composables/useMultiTechSelect";
import { fullAddress } from "@/mixins";
import { Address } from "@/typings/address";
import { Customer } from "@/typings/customer";
import { Job } from "@/typings/job";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import AppointmentAvailabilityInput from "./AppointmentAvailabilityInput.vue";
import CustomerSearchInput from "./CustomerSearchInput.vue";
import { useAppointmentSchedulingFields } from "./useAppointmentSchedulingFields";

const emit = defineEmits(["hide-modal", "created"]);

const props = defineProps<{
  scheduled_time?: string;
  arrival_window_start?: string;
  arrival_window_end?: string;
  unavailable_time?: boolean;
  service_category_id?: number | null;
  technician_id?: number | null;
}>();

const formData = ref<{
  scheduled_time: string;
  customer_id: number | null;
  address_id: number | null;
  service_category_id: number | null;
  technician_id: number | number[] | null;
  notes: string;
  lead_type: string | null;
  lead_source: string | null;
  skip_notifications: boolean;
  arrival_window_start: string;
  arrival_window_end: string;
  unavailable_time: boolean;
}>({
  scheduled_time: props.scheduled_time || "",
  service_category_id: props.service_category_id || null,
  technician_id: props.technician_id || null,
  arrival_window_start: props.arrival_window_start || "",
  arrival_window_end: props.arrival_window_end || "",
  unavailable_time: props.unavailable_time || false,
  customer_id: null,
  address_id: null,
  notes: "",
  lead_type: null,
  lead_source: null,
  skip_notifications: false
});

const store = useStore();
const selectedAddress = ref<Address | null>(null);
const selectedCustomer = ref<Customer | null>(null);
const saving = ref(false);
const allTechs = ref(false);

const selectedTechIds = computed(() => {
  const id = formData.value.technician_id;
  if (Array.isArray(id)) return id;
  if (id != null) return [id];
  return [];
});

const { isMultiTechEnabled, limitHelperText, toTechProfileIds, MAX_TECHS } =
  useMultiTechSelect(selectedTechIds);

const {
  serviceCategoryOptions,
  technicianOptions,
  selectedServiceCategory,
  selectedTech
} = useAppointmentSchedulingFields(formData, allTechs);

const v$ = useVuelidate(
  {
    scheduled_time: {
      required: helpers.withMessage("Scheduled time is required", required)
    },
    customer_id: {
      required: helpers.withMessage("Customer is required", required)
    },
    address_id: {
      required: helpers.withMessage("Address is required", required)
    },
    service_category_id: {
      required: helpers.withMessage("Service category is required", required)
    }
  },
  formData.value
);

onMounted(() => {
  prepopulateForm();
});

const handleTechnicianSelect = (val: number | number[] | null) => {
  if (Array.isArray(val) && val.length > MAX_TECHS) return;
  formData.value.technician_id = val;
};

const prepopulateForm = () => {
  formData.value.scheduled_time = props.scheduled_time || "";
  formData.value.service_category_id = props.service_category_id || null;
  if (isMultiTechEnabled.value && props.technician_id) {
    formData.value.technician_id = [props.technician_id];
  } else {
    formData.value.technician_id = props.technician_id || null;
  }
  formData.value.arrival_window_start = props.arrival_window_start || "";
  formData.value.arrival_window_end = props.arrival_window_end || "";
  formData.value.unavailable_time = props.unavailable_time || false;
};

const handleCustomerSelect = (result: { customer: any; address: Address }) => {
  formData.value.customer_id = result.customer.id;
  formData.value.address_id = result.address.id;
  selectedCustomer.value = result.customer;
  selectedAddress.value = result.address;
};

const handleSave = async () => {
  saving.value = true;
  try {
    const valid = await v$.value.$validate();
    if (valid) {
      const techPayload = isMultiTechEnabled.value
        ? {
            technician_profile_ids: toTechProfileIds(selectedTechIds.value)
          }
        : { user_id: formData.value.technician_id };

      const resp: { data: Job } = await store.dispatch("createJob", {
        link: {
          href: selectedAddress.value?.links?.job_create,
          method: "POST"
        },
        payload: {
          address_id: formData.value.address_id,
          appointment: {
            scheduled_time: formData.value.scheduled_time,
            arrival_window_start: formData.value.arrival_window_start,
            arrival_window_end: formData.value.arrival_window_end,
            unavailable_time: formData.value.unavailable_time,
            ...techPayload,
            notes: formData.value.notes,
            skip_notifications: formData.value.skip_notifications,
            service_category_id: formData.value.service_category_id
          },
          customer_id: formData.value.customer_id,
          lead_source: formData.value.lead_source || "",
          lead_type: formData.value.lead_type || "",
          tags: []
        }
      });
      const appointmentId = resp?.data?.appointments?.[0]?.id;
      window.notify.success(
        appointmentId
          ? `Appointment created. <div class="italic inline underline" onclick="window.handleSchedulerEventEdit && window.handleSchedulerEventEdit(${appointmentId})">View Appointment</div>`
          : "Appointment created."
      );
      EventBus.$emit("refresh-schedule-board");
      emit("hide-modal");
      emit("created");
    }
  } catch (e) {
    console.error(e);
  }
  saving.value = false;
};

const handleTimeSelected = (data: {
  scheduled_time: string;
  arrival_window_start: string;
  arrival_window_end: string;
  unavailable_time: boolean;
}) => {
  formData.value.scheduled_time = data.scheduled_time;
  formData.value.arrival_window_start = data.arrival_window_start;
  formData.value.arrival_window_end = data.arrival_window_end;
  formData.value.unavailable_time = data.unavailable_time;
};
</script>

<style scoped></style>
